// src/email/pages/MailboxPage.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  useConnectGmail,
  useDisconnectAccount,
  useEmailAccounts,
  useSearchThreads,
  useSyncAccount,
  useThreads,
} from '../api/hooks';
import { SearchBar } from '../components/SearchBar';
import { ComposeModal } from '../components/ComposeModal';
import { ConfirmModal } from '../components/ConfirmModal';
import { FolderSidebar } from '../components/FolderSidebar';
import { ThreadList } from '../components/ThreadList';
import { ThreadView } from '../components/ThreadView';
import { useEmailSocket } from '../socket/useEmailSocket';
import { EMAIL_APIS_DISABLED } from '../api/client';
import '../styles/email.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

function MailboxContent() {
  const qc = useQueryClient();
  useEmailSocket(qc);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: accounts = [], isLoading: accountsLoading } = useEmailAccounts();
  const connect = useConnectGmail();
  const disconnect = useDisconnectAccount();
  const syncAccount = useSyncAccount();

  const queryAccountId = searchParams.get('accountId');
  const [activeAccountId, setActiveAccountId] = useState(queryAccountId || undefined);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [activeThreadId, setActiveThreadId] = useState(undefined);
  const [compose, setCompose] = useState(null);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [pendingAccountEmail, setPendingAccountEmail] = useState(
    searchParams.get('account') || null
  );
  const accountMenuRef = useRef(null);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sg_mailbox_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('sg_mailbox_sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'primary',
    icon: 'users',
    onConfirm: () => {},
  });

  // Close account switcher on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setIsAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Remove outer margin/padding on CRM .content for full-bleed edge-to-edge layout
  useEffect(() => {
    const contentEl = document.querySelector('.content');
    if (contentEl) {
      contentEl.classList.add('email-content-flush');
      return () => {
        contentEl.classList.remove('email-content-flush');
      };
    }
  }, []);

  // The mailbox in the URL (?accountId=..) is the single source of truth, so the sidebar
  // menu and the top dropdown always point at the same mailbox
  const selectAccount = (accountId, { keepThread = false } = {}) => {
    setActiveAccountId(accountId);
    if (!keepThread) setActiveThreadId(undefined);
    const next = new URLSearchParams(searchParams);
    next.set('accountId', String(accountId));
    setSearchParams(next, { replace: true });
  };

  // Follow the mailbox picked from the sidebar, or fall back to the first account
  useEffect(() => {
    if (queryAccountId && String(queryAccountId) !== String(activeAccountId)) {
      setActiveAccountId(queryAccountId);
      setActiveThreadId(undefined);
    } else if (!queryAccountId && accounts.length > 0) {
      // No mailbox in the URL yet: put the current one there so the sidebar highlights it
      selectAccount(activeAccountId || accounts[0].id, { keepThread: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, queryAccountId, activeAccountId]);

  // Dispatch active account change so socket and listeners can subscribe
  useEffect(() => {
    if (activeAccountId) {
      window.dispatchEvent(new CustomEvent('emailAccountSelected', { detail: activeAccountId }));
    }
  }, [activeAccountId]);

  // When accounts list loads or updates, if we have a pending account to select, activate it!
  useEffect(() => {
    if (pendingAccountEmail && accounts.length > 0) {
      const found = accounts.find(
        (a) => a.emailAddress?.toLowerCase() === pendingAccountEmail.toLowerCase()
      );
      if (found) {
        selectAccount(found.id);
        setPendingAccountEmail(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, pendingAccountEmail]);

  // Handle OAuth callback parameters (?connect=success | ?connect=error)
  useEffect(() => {
    const connectParam = searchParams.get('connect');
    const accountParam = searchParams.get('account');
    const accountIdParam = searchParams.get('accountId');

    if (connectParam === 'success') {
      qc.invalidateQueries({ queryKey: ['email-accounts'] });
      window.dispatchEvent(new CustomEvent('emailAccountsUpdated'));

      if (accountIdParam) {
        setActiveAccountId(accountIdParam);
        setActiveThreadId(undefined);
      } else if (accountParam) {
        setPendingAccountEmail(accountParam);
      }

      // accountId stays in the URL so the newly connected mailbox remains selected
      searchParams.delete('connect');
      searchParams.delete('account');
      setSearchParams(searchParams, { replace: true });
    } else if (connectParam === 'error') {
      const reason = searchParams.get('reason') || 'Unknown error occurred';
      alert(`Failed to connect Gmail: ${reason}`);
      searchParams.delete('connect');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, qc]);

  const activeAccount =
    accounts.find((a) => String(a.id) === String(activeAccountId)) || accounts[0];
  const isOwner = activeAccount?.isOwner !== false;

  const [pageCursor, setPageCursor] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);

  // Reset pagination when account or folder changes (not on mount, so a
  // deep-linked thread survives — including StrictMode's double effect run)
  const scopeRef = useRef({ accountId: activeAccountId, folder: activeFolder });
  useEffect(() => {
    const prev = scopeRef.current;
    if (prev.accountId === activeAccountId && prev.folder === activeFolder) return;
    scopeRef.current = { accountId: activeAccountId, folder: activeFolder };
    setPageCursor(null);
    setCursorHistory([null]);
    setPageIndex(0);
    setActiveThreadId(undefined);
  }, [activeAccountId, activeFolder]);

  // Deep link (?accountId=..&thread=..): open that conversation
  useEffect(() => {
    const threadParam = searchParams.get('thread');
    if (!threadParam) return;
    setActiveThreadId(/^\d+$/.test(threadParam) ? Number(threadParam) : threadParam);
    const next = new URLSearchParams(searchParams);
    next.delete('thread');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Search: searchInput is what's typed, searchQuery is what's actually being searched
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const isSearching = searchQuery.length > 0;

  // Auto-run 300ms after typing stops (2+ characters); emptying the box exits search at once
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (!trimmed) {
      setSearchQuery('');
      return undefined;
    }
    if (trimmed.length < 2) return undefined;
    const timer = setTimeout(() => setSearchQuery(trimmed), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const runSearch = (text) => {
    setSearchInput(text);
    setSearchQuery(text.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const {
    data: threadsPage,
    isLoading: threadsLoading,
    isFetching: threadsFetching,
  } = useThreads(isSearching ? undefined : activeAccount?.id, activeFolder, pageCursor, 50);

  const search = useSearchThreads(activeAccount?.id, searchQuery, 50);
  const searchThreads = useMemo(() => {
    const seen = new Set();
    return (search.data?.pages || [])
      .flatMap((p) => p?.items || [])
      .filter((t) => (seen.has(t.id) ? false : seen.add(t.id)));
  }, [search.data]);

  const threads = isSearching ? searchThreads : threadsPage?.items || [];
  const hasMore = isSearching
    ? Boolean(search.hasNextPage)
    : Boolean(threadsPage?.hasMore || threadsPage?.nextCursor);
  const totalCount = activeAccount?.counts?.[activeFolder] ?? null;

  const handleNextPage = () => {
    if (threadsPage?.nextCursor) {
      setCursorHistory((prev) => [...prev.slice(0, pageIndex + 1), threadsPage.nextCursor]);
      setPageCursor(threadsPage.nextCursor);
      setPageIndex((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (pageIndex > 0) {
      const prevCursor = cursorHistory[pageIndex - 1];
      setPageCursor(prevCursor);
      setPageIndex((prev) => prev - 1);
    }
  };

  const currentThreadIndex = threads.findIndex((t) => t.id === activeThreadId);
  const prevThreadId = currentThreadIndex > 0 ? threads[currentThreadIndex - 1]?.id : null;
  const nextThreadId =
    currentThreadIndex >= 0 && currentThreadIndex < threads.length - 1
      ? threads[currentThreadIndex + 1]?.id
      : null;

  const handleDisconnect = () => {
    if (!activeAccount) return;
    setConfirmConfig({
      isOpen: true,
      title: 'Disconnect Mailbox?',
      message: (
        <>
          Are you sure you want to disconnect <strong>{activeAccount.emailAddress}</strong>?
          <span className="confirm-modal-subtext">
            This will remove the mailbox from your CRM. You will need to re-authenticate with Google to reconnect it.
          </span>
        </>
      ),
      confirmText: 'Disconnect',
      variant: 'danger',
      icon: 'disconnect',
      onConfirm: () => {
        disconnect.mutate(activeAccount.id, {
          onSettled: () => setConfirmConfig((prev) => ({ ...prev, isOpen: false })),
        });
      },
    });
  };

  return (
    <div className="email-module-container">
      {EMAIL_APIS_DISABLED && (
        <div
          style={{
            backgroundColor: '#fff8e6',
            color: '#8a6d3b',
            borderBottom: '1px solid #faebcc',
            padding: '10px 20px',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: '16px' }}>ℹ️</span>
          <span>
            <strong>Notice:</strong> Email module APIs are temporarily turned off.
          </span>
        </div>
      )}
      <header className="topbar">
        <div className="topbar-left">
          {activeAccount && (
            <div className="account-switcher-wrapper" ref={accountMenuRef}>
              <button
                type="button"
                className="active-account-pill active-account-pill-clickable"
                onClick={() => setIsAccountMenuOpen((prev) => !prev)}
                title="Click to switch mailbox"
              >
                <div className="account-pill-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
                  </svg>
                </div>
                <span className="active-account-email">{activeAccount.emailAddress}</span>
                <svg
                  className={`account-caret ${isAccountMenuOpen ? 'open' : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {isAccountMenuOpen && (
                <div className="account-dropdown-menu">
                  <div className="account-dropdown-header">
                    <span>Connected Mailboxes ({accounts.length})</span>
                  </div>
                  <div className="account-dropdown-list">
                    {accounts.map((acct) => {
                      const isCurrent = String(acct.id) === String(activeAccount.id);
                      return (
                        <div
                          key={acct.id}
                          className={`account-dropdown-item ${isCurrent ? 'is-active' : ''}`}
                          onClick={() => {
                            selectAccount(acct.id);
                            setIsAccountMenuOpen(false);
                          }}
                        >
                          <div className="dropdown-item-left">
                            <span className="dropdown-item-radio">
                              {isCurrent ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="#4f46e5">
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="m9 12 2 2 4-4" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                </svg>
                              ) : (
                                <span className="dropdown-radio-circle" />
                              )}
                            </span>
                            <div className="dropdown-item-info">
                              <span className="dropdown-item-email">{acct.emailAddress}</span>
                              {acct.counts?.unread > 0 && (
                                <div className="dropdown-item-tags">
                                  <span className="badge-unread-count">
                                    {acct.counts.unread} unread
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          {isCurrent && (
                            <span className="dropdown-current-tag">Active</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="account-dropdown-footer">
                    <button
                      type="button"
                      className="btn-dropdown-connect"
                      onClick={() => {
                        setIsAccountMenuOpen(false);
                        connect.mutate();
                      }}
                      disabled={connect.isPending}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      <span>Connect Another Email</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeAccount?.id && (
            <button
              className={`btn-sync-now ${syncAccount.isPending ? 'is-syncing' : ''}`}
              onClick={() => syncAccount.mutate(activeAccount.id)}
              disabled={syncAccount.isPending}
              title="Sync latest emails from Gmail now"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                className={syncAccount.isPending ? 'spin' : ''}
              >
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
              </svg>
              <span>{syncAccount.isPending ? 'Syncing…' : 'Sync Now'}</span>
            </button>
          )}
        </div>

        <div className="topbar-actions">


          {/* Connect Another Account Button */}
          <button
            className="btn btn-connect-topbar"
            onClick={() => connect.mutate()}
            disabled={connect.isPending}
            title="Connect a new Gmail account"
          >
            {connect.isPending ? (
              <>
                <span className="mini-spinner" />
                <span>Connecting…</span>
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>Connect Email</span>
              </>
            )}
          </button>

          {/* Compose Email Button */}
          <button
            className="btn btn-primary btn-compose-topbar"
            disabled={!activeAccount}
            onClick={() => setCompose({ mode: 'new', source: null })}
            title="Compose new email"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            <span>Compose Email</span>
          </button>
        </div>
      </header>

      <div className={`mailbox-body ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <aside className={`mailbox-left ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          {activeAccount ? (
            <div className={`folders-section ${isSidebarCollapsed ? 'collapsed' : ''}`}>
              <div className={`folders-header ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                {!isSidebarCollapsed && <span>Folders</span>}
                <button
                  type="button"
                  className="btn-sidebar-toggle"
                  onClick={toggleSidebar}
                  title={isSidebarCollapsed ? 'Expand sidebar (show labels)' : 'Collapse sidebar (icons only)'}
                  aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {isSidebarCollapsed ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  ) : (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  )}
                </button>
              </div>
              <FolderSidebar
                counts={activeAccount.counts}
                activeFolder={isSearching ? null : activeFolder}
                isCollapsed={isSidebarCollapsed}
                onSelect={(f) => {
                  setActiveFolder(f);
                  setActiveThreadId(undefined);
                  clearSearch();
                }}
              />
              {isOwner && (
                <div className={`sidebar-bottom ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                  <button
                    type="button"
                    className={`btn btn-disconnect-filled ${isSidebarCollapsed ? 'collapsed' : ''}`}
                    onClick={handleDisconnect}
                    disabled={disconnect.isPending}
                    title={`Disconnect ${activeAccount.emailAddress}`}
                    aria-label={`Disconnect ${activeAccount.emailAddress}`}
                  >
                    {disconnect.isPending ? (
                      <>
                        <span className="mini-spinner-white" />
                        {!isSidebarCollapsed && <span>Disconnecting…</span>}
                      </>
                    ) : (
                      <>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                          <line x1="12" y1="2" x2="12" y2="12" />
                        </svg>
                        {!isSidebarCollapsed && <span>Disconnect</span>}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : accountsLoading ? (
            <div className="empty-hint">{isSidebarCollapsed ? '…' : 'Loading mailboxes…'}</div>
          ) : (
            <div className="empty-mailbox-card">
              <div className="empty-mailbox-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <h4>No Email Connected</h4>
              <p>Connect your Gmail account to manage and read emails directly here.</p>
              <button
                className="btn btn-primary"
                onClick={() => connect.mutate()}
                disabled={connect.isPending}
              >
                + Connect Gmail
              </button>
            </div>
          )}
        </aside>

        <main className="mailbox-main-content">
          {!activeThreadId && activeAccount && (
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              onSubmit={runSearch}
              onClear={clearSearch}
            />
          )}
          {!activeThreadId ? (
            <ThreadList
              threads={threads}
              activeThreadId={activeThreadId}
              onSelect={(id) => setActiveThreadId(id)}
              isLoading={isSearching ? search.isLoading : threadsLoading}
              isFetching={isSearching ? search.isFetchingNextPage : threadsFetching}
              activeFolderName={activeFolder}
              onRefresh={() => syncAccount.mutate(activeAccount?.id)}
              isRefreshing={syncAccount.isPending}
              pageIndex={pageIndex}
              pageSize={50}
              totalCount={totalCount}
              hasMore={hasMore}
              onNextPage={isSearching ? () => search.fetchNextPage() : handleNextPage}
              onPrevPage={handlePrevPage}
              searchQuery={searchQuery}
              isSearchError={isSearching && search.isError}
              isSearchStale={isSearching && search.isPlaceholderData}
              onRetrySearch={() => search.refetch()}
            />
          ) : (
            <ThreadView
              threadId={activeThreadId}
              activeFolderName={isSearching ? 'search results' : activeFolder}
              onBack={() => setActiveThreadId(undefined)}
              onCompose={(mode, source) => setCompose({ mode, source })}
              currentIndex={currentThreadIndex >= 0 ? currentThreadIndex : undefined}
              totalThreads={threads.length}
              onPrevThread={prevThreadId ? () => setActiveThreadId(prevThreadId) : null}
              onNextThread={nextThreadId ? () => setActiveThreadId(nextThreadId) : null}
            />
          )}
        </main>
      </div>

      {compose && activeAccount && (
        <ComposeModal
          account={activeAccount}
          accounts={accounts}
          mode={compose.mode}
          sourceMessage={compose.source}
          onClose={() => setCompose(null)}
          onDiscarded={() => {
            // The conversation may no longer be listed under Drafts
            if (activeFolder === 'drafts') setActiveThreadId(undefined);
          }}
          onSent={() => {
            setActiveFolder('sent');
            setActiveThreadId(undefined);
            clearSearch();
          }}
        />
      )}

      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        variant={confirmConfig.variant}
        icon={confirmConfig.icon}
        isPending={disconnect.isPending}
        onConfirm={confirmConfig.onConfirm}
        onClose={() => {
          if (!disconnect.isPending) {
            setConfirmConfig((prev) => ({ ...prev, isOpen: false }));
          }
        }}
      />
    </div>
  );
}

export default function MailboxPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <MailboxContent />
    </QueryClientProvider>
  );
}
